import * as React from "react";
import { useState } from "react";
import { Alert, FormControl, Grid, InputLabel, OutlinedInput, Stack } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";

import {
	useCreatePlugin,
	usePlugin,
	usePluginTypes,
	usePluginTypeSettings,
	useUpdatePlugin,
} from "@/hooks/api/use-plugins";
import { useOAuthStatus } from "@/hooks/api/use-oauth";
import { GoogleCalendarOperations } from "@/components/calendar/google-calendar-operations";
import { ComponentLoader } from "@/components/componentLoader/component-loader";
import { CustomDrawer } from "@/components/custom-drawer";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { DynamicField } from "@/components/dynamicField/dynamic-field";
import { DynamicFieldType } from "@/components/dynamicField/types";

import { OAuthConnection } from "./oauth-connection";

export function CreatePlugin({
	isCreatePluginDrawerOpen,
	onCreateDrawerClose,
	pluginId,
}: {
	isCreatePluginDrawerOpen: boolean;
	onCreateDrawerClose: () => void;
	pluginId: string | null;
}): React.JSX.Element {
	// An OAuth plugin stays open after create: its account can only be connected once it is saved.
	const [createdPluginId, setCreatedPluginId] = useState<string | null>(null);
	const activePluginId = pluginId || createdPluginId;
	const isEdit = !!activePluginId;
	React.useEffect(() => {
		if (!isCreatePluginDrawerOpen) setCreatedPluginId(null);
	}, [isCreatePluginDrawerOpen]);

	const { data: plugin, isLoading: isPluginLoading } = usePlugin(activePluginId || "");
	const { data: pluginTypes = [], isLoading: isPluginTypesLoading } = usePluginTypes();

	const [selectedPluginType, setSelectedPluginType] = useState<string>("");
	const { data: pluginTypeSettingsData, isLoading: isPluginTypeSettingsLoading } =
		usePluginTypeSettings(selectedPluginType);
	const isOAuth = !!pluginTypes.find((type) => type.name === selectedPluginType)?.services?.includes("oauth");
	const { data: oauthStatus } = useOAuthStatus(isOAuth ? activePluginId || "" : "");

	const createPluginMutation = useCreatePlugin();
	const updatePluginMutation = useUpdatePlugin();

	const isLoading = isPluginLoading || isPluginTypesLoading || isPluginTypeSettingsLoading;

	const methods = useForm<any>({
		defaultValues: {
			title: "",
			enabled: false,
			plugin: "",
		},
	});

	const {
		handleSubmit,
		reset,
		control,
		setError,
		setValue,
		getValues,
		formState: { isSubmitting },
	} = methods;

	React.useLayoutEffect(() => {
		if (plugin && isEdit) {
			reset({
				title: plugin.title,
				enabled: plugin.enabled,
				plugin: plugin.plugin.name,
				settings: plugin.settings,
			});
			setSelectedPluginType(plugin.plugin.name);
		} else {
			reset({
				title: "",
				enabled: false,
				plugin: "",
				settings: {},
			});
      setSelectedPluginType("");
		}
	}, [plugin, isEdit, reset, isCreatePluginDrawerOpen]);

	const handleChangePluginType = (value: string) => {
		const settings = getValues("settings");

		if (settings) {
			for (const key of Object.keys(settings)) {
				setValue(`settings.${key}`, null);
			}
		}
		setValue("settings", {});
		setSelectedPluginType(value);
	};

	const onSubmitForm = async (data: any) => {
		try {
			const saved = await (isEdit && plugin?.id
				? updatePluginMutation.mutateAsync({ id: plugin.id, data })
				: createPluginMutation.mutateAsync(data));

			if (isOAuth && !isEdit) setCreatedPluginId(saved.id);
			else onCreateDrawerClose();
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;
			if (!Array.isArray(errorMessages)) {
				return;
			}

			const errorMessage = errorMessages[0];
			setError(errorMessage.split(" ")[0], { message: errorMessage });
		}
	};

	return (
		<React.Fragment>
			<CustomDrawer
				open={isCreatePluginDrawerOpen}
				onClose={onCreateDrawerClose}
				title={isEdit ? "Edit Plugin" : "Create Plugin"}
				onSubmit={handleSubmit(onSubmitForm)}
				isSubmitButtonDisabled={isSubmitting || createPluginMutation.isPending || updatePluginMutation.isPending}
				submitButtonText={isEdit ? "Update Plugin" : "Create Plugin"}
      	width="420px"
			>
				<ComponentLoader loading={isLoading} />
				<FormProvider {...methods}>
					<Grid container spacing={2}>
						<Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							{" "}
							<Controller
								control={control}
								name="title"
								rules={{
									required: "Title is required",
									minLength: {
										value: 2,
										message: "Title must be at least 2 characters",
									},
								}}
								render={({ field, fieldState }) => (
									<FormControl error={Boolean(fieldState.error)} fullWidth required>
										<InputLabel>Title</InputLabel>
										<OutlinedInput {...field} placeholder="Enter title" />
										{fieldState.error && <CustomFormHelperError text={fieldState.error.message || ""} />}
									</FormControl>
								)}
							/>
						</Grid>

						<Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							<DynamicField
								control={control}
								type={DynamicFieldType.SELECT}
								name="plugin"
								label="Plugin Type"
								onChange={handleChangePluginType}
								disabled={isEdit}
								dynamicField={{
									name: "plugin",
									required: true,
									type: DynamicFieldType.SELECT,
									label: "Plugin Type",
									placeholder: "Select a plugin type",
									options: pluginTypes.map((item: any) => ({ value: item.name, label: item.title })),
								}}
							/>
						</Grid>

						<Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							<DynamicField
								control={control}
								type={DynamicFieldType.BOOLEAN}
								name="enabled"
								label="Enabled"
								dynamicField={{
									name: "enabled",
									type: DynamicFieldType.BOOLEAN,
									label: "Enabled",
									// The backend switches the plugin off while no account is connected.
									description:
										isOAuth && oauthStatus?.status !== "connected"
											? "Connect an account before enabling this plugin."
											: undefined,
								}}
							/>
						</Grid>

						{pluginTypeSettingsData?.fields?.map((field: any) => (
							<Grid
								key={`${pluginTypeSettingsData}-${field.name}`}
								size={{
									md: 12,
									xs: 12,
								}}
							>
								<DynamicField
									control={control}
									type={field.type}
									name={`settings.${field.name}`}
									label={field.label}
									dynamicField={field}
									formName="settings"
								/>
							</Grid>
						))}

						{isOAuth && (
							<Grid
								size={{
									md: 12,
									xs: 12,
								}}
							>
								{isEdit ? (
									plugin && (
										<Stack spacing={2}>
											<OAuthConnection pluginId={plugin.id} />
											{plugin.plugin.name === "google-calendar" && (
												<GoogleCalendarOperations pluginId={plugin.id} enabled={plugin.enabled} />
											)}
										</Stack>
									)
								) : (
									<Alert severity="info">Save this plugin, then connect the account here.</Alert>
								)}
							</Grid>
						)}
					</Grid>
				</FormProvider>
			</CustomDrawer>
		</React.Fragment>
	);
}
